/**
 * Procedural starfield (deterministic seed, magnitude-like size distribution). It lives in its own scene that only
 * shares the camera's rotation, so stars are infinitely far away and never move with the floating origin.
 */
import * as THREE from 'three';
import type { StarsFile } from '../core/data';
import { mulberry32, randomDirection } from '../core/random';
import { bvToTemperature, starAppearance, temperatureToRgb } from '../core/stars';
import { discTexture } from './textures';

const STAR_COUNT = 4000;

export function createStarfield(): THREE.Scene {
  const scene = new THREE.Scene();
  const disc = discTexture();
  const rand = mulberry32(19_771_977);
  // Three brightness classes: many faint, some medium, few bright (roughly like a magnitude distribution).
  const classes = [
    { share: 0.8, size: 1.2, brightness: 0.45 },
    { share: 0.17, size: 1.8, brightness: 0.75 },
    { share: 0.03, size: 2.6, brightness: 1 },
  ];
  for (const cls of classes) {
    const n = Math.round(STAR_COUNT * cls.share);
    const pos = new Float32Array(3 * n);
    const col = new Float32Array(3 * n);
    for (let i = 0; i < n; i++) {
      const [x, y, z] = randomDirection(rand);
      pos.set([x * 100, y * 100, z * 100], 3 * i);
      // slight colour temperature variation: bluish to warm white
      const temp = rand();
      const b = cls.brightness * (0.7 + 0.3 * rand());
      col.set([b * (0.85 + 0.15 * temp), b * (0.9 + 0.05 * temp), b * (1.05 - 0.2 * temp)], 3 * i);
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geom.setAttribute('color', new THREE.BufferAttribute(col, 3));
    const mat = new THREE.PointsMaterial({
      size: cls.size * 1.6,
      map: disc,
      alphaTest: 0.05,
      sizeAttenuation: false,
      vertexColors: true,
      depthWrite: false,
      transparent: true,
    });
    scene.add(new THREE.Points(geom, mat));
  }
  return scene;
}

/**
 * Real sky from the Yale Bright Star Catalogue (S1-24): per-star size and colour (B−V) in one draw call.
 * Sizes are CSS px; `uDpr` scales them to device pixels.
 */
export function createCatalogStars(file: StarsFile, dpr: number): THREE.Points {
  const n = file.count;
  const pos = new Float32Array(3 * n);
  const col = new Float32Array(3 * n);
  const size = new Float32Array(n);
  const linear = new THREE.Color();
  for (let i = 0; i < n; i++) {
    const k = 5 * i;
    pos[3 * i] = (file.data[k]! / 1e4) * 100;
    pos[3 * i + 1] = (file.data[k + 1]! / 1e4) * 100;
    pos[3 * i + 2] = (file.data[k + 2]! / 1e4) * 100;
    const mag = file.data[k + 3]! / 100;
    const [r, g, b] = temperatureToRgb(bvToTemperature(file.data[k + 4]! / 100));
    const look = starAppearance(mag);
    // desaturate a little: stars look mostly white to the eye. The colour is authored in sRGB and stored linear, so
    // it renders the same straight to the screen and through the HDR post-processing target (S2-07).
    linear.setRGB(
      (0.55 + 0.45 * r) * look.alpha,
      (0.55 + 0.45 * g) * look.alpha,
      (0.55 + 0.45 * b) * look.alpha,
      THREE.SRGBColorSpace,
    );
    col[3 * i] = linear.r;
    col[3 * i + 1] = linear.g;
    col[3 * i + 2] = linear.b;
    size[i] = look.size;
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geom.setAttribute('color', new THREE.BufferAttribute(col, 3));
  geom.setAttribute('size', new THREE.BufferAttribute(size, 1));
  const material = new THREE.ShaderMaterial({
    uniforms: { uDpr: { value: dpr } },
    vertexShader: /* glsl */ `
      attribute float size;
      attribute vec3 color;
      uniform float uDpr;
      varying vec3 vColor;
      void main() {
        vColor = color;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * uDpr;
      }`,
    fragmentShader: /* glsl */ `
      varying vec3 vColor;
      void main() {
        float d = length(gl_PointCoord - 0.5);
        float a = 1.0 - smoothstep(0.25, 0.5, d); // edge0 < edge1 (reversed edges are undefined in GLSL)
        if (a <= 0.0) discard;
        gl_FragColor = vec4(vColor * a, 1.0);
        #include <colorspace_fragment>
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(geom, material);
  points.name = 'catalog-stars';
  return points;
}
